import { useState, useCallback, useEffect } from 'react'
import { router } from '@inertiajs/react'
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core'
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import KanbanColumn from './KanbanColumn'
import TaskCard from './TaskCard'
import TaskDrawer from './TaskDrawer'

interface TaskLabel {
  id: string
  name: string
  color: string
}

interface TaskItem {
  id: string
  title: string
  description: string | null
  position: number
  priority: 'no_priority' | 'low' | 'medium' | 'high' | 'urgent'
  assignee: { id: string; name: string } | null
  labels: TaskLabel[]
  updated_at: string
}

interface Column {
  id: string
  name: string
  color: string | null
  position: number
  tasks: TaskItem[]
}

interface BoardDetail {
  id: string
  name: string
  slug: string
  description: string | null
  columns: Column[]
}

interface UserOption {
  id: string
  name: string
  email: string
}

interface Props {
  board: BoardDetail
  users: UserOption[]
}

export default function KanbanBoard({ board, users }: Props) {
  const [columns, setColumns] = useState(board.columns)
  const [activeTask, setActiveTask] = useState<TaskItem | null>(null)

  // Sync local state when Inertia delivers new props
  useEffect(() => {
    setColumns(board.columns)
  }, [board.columns])
  const [selectedTask, setSelectedTask] = useState<TaskItem | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const findTask = useCallback((taskId: string) => {
    for (const col of columns) {
      const task = col.tasks.find(t => t.id === taskId)
      if (task) return { task, column: col }
    }
    return null
  }, [columns])

  const handleDragStart = (event: DragStartEvent) => {
    const found = findTask(event.active.id as string)
    if (found) setActiveTask(found.task)
  }

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event
    if (!over) return

    const activeId = active.id as string
    const overId = over.id as string

    const activeData = findTask(activeId)
    if (!activeData) return

    // Determine target column
    let targetColumnId: string | null = null
    const overData = findTask(overId)
    if (overData) {
      targetColumnId = overData.column.id
    } else {
      // Dropped on a column directly
      const col = columns.find(c => c.id === overId)
      if (col) targetColumnId = col.id
    }

    if (!targetColumnId || activeData.column.id === targetColumnId) return

    setColumns(prev => {
      const newCols = prev.map(c => ({
        ...c,
        tasks: c.tasks.filter(t => t.id !== activeId)
      }))
      const targetCol = newCols.find(c => c.id === targetColumnId)
      if (targetCol) {
        targetCol.tasks.push(activeData.task)
      }
      return newCols
    })
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setActiveTask(null)

    if (!over) return

    const activeId = active.id as string
    const overId = over.id as string

    // Find the task's current column after drag-over changes
    const activeData = findTask(activeId)
    if (!activeData) return

    let targetPosition = 0
    const overData = findTask(overId)
    if (overData && overId !== activeId) {
      targetPosition = overData.task.position
    }

    // Send move to server
    router.patch(`/tasks/${activeId}/move`, {
      board_column_id: activeData.column.id,
      position: targetPosition,
    }, { preserveState: true, preserveScroll: true })
  }

  const handleCreateTask = (columnId: string, title: string) => {
    router.post(`/boards/${board.slug}/tasks`, {
      task: { title, board_column_id: columnId }
    }, { preserveScroll: true })
  }

  const handleTaskClick = (task: TaskItem) => {
    setSelectedTask(task)
    setDialogOpen(true)
  }

  const handleDeleteTask = (task: TaskItem) => {
    router.delete(`/tasks/${task.id}`, { preserveScroll: true })
  }

  const handleTaskUpdated = useCallback((updatedTask: TaskItem) => {
    setColumns(prev => prev.map(col => ({
      ...col,
      tasks: col.tasks.map(t => t.id === updatedTask.id ? { ...t, ...updatedTask } : t),
    })))
    setSelectedTask(prev => prev?.id === updatedTask.id ? { ...prev, ...updatedTask } : prev)
  }, [])

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 overflow-x-auto pb-4">
        {columns.map(column => (
          <KanbanColumn
            key={column.id}
            column={column}
            onCreateTask={handleCreateTask}
            onTaskClick={handleTaskClick}
            onDeleteTask={handleDeleteTask}
          />
        ))}
      </div>

      <DragOverlay>
        {activeTask && <TaskCard task={activeTask} isDragging />}
      </DragOverlay>

      <TaskDrawer
        task={selectedTask}
        users={users}
        boardSlug={board.slug}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onTaskUpdated={handleTaskUpdated}
      />
    </DndContext>
  )
}
